import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';

export default function Login() {
  const { login, completeNewPassword, forgotPassword, confirmForgotPassword } = useAuth();
  const api = useApi();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login'); // login | newpassword | forgot | forgotconfirm
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPw, setNewPw] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingCognitoUser, setPendingCognitoUser] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.newPasswordRequired) {
        setPendingCognitoUser(result.cognitoUser);
        setDisplayName(result.userAttributes?.name || '');
        setMode('newpassword');
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const user = await completeNewPassword(pendingCognitoUser, newPw, displayName.trim() || undefined);
      // Sync display name to DynamoDB profile (fire and forget — don't block navigation)
      if (user?.sub && displayName.trim()) {
        api.updateProfile(user.sub, { name: displayName.trim() }).catch(() => {});
      }
      navigate('/');
    } catch (err) {
      setError(err.message || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setLoading(true);
    try {
      await forgotPassword(email);
      setInfo('Check your email for a reset code.');
      setMode('forgotconfirm');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await confirmForgotPassword(email, code, newPw);
      setInfo('Password reset. Please sign in.');
      setMode('login');
      setPassword('');
      setCode('');
      setNewPw('');
    } catch (err) {
      setError(err.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          <h1>WC 2026</h1>
          <p>Predictor League</p>
        </div>

        {error && <div className="error-msg" style={{ marginBottom: '1rem' }}>{error}</div>}
        {info  && <div style={{ marginBottom: '1rem', color: 'var(--volt)', fontSize: '0.9rem', textAlign: 'center' }}>{info}</div>}

        {mode === 'login' && (
          <form className="login-form" onSubmit={handleLogin}>
            <div className="field">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="field">
              <label>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <button type="button" className="btn-link" onClick={() => { setError(''); setInfo(''); setMode('forgot'); }}>
                Forgot password?
              </button>
            </p>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
              Don't have an account?{' '}
              <Link to="/join" className="btn-link">Request access</Link>
            </p>
          </form>
        )}

        {mode === 'newpassword' && (
          <form className="login-form" onSubmit={handleNewPassword}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              Welcome! Set your display name and a permanent password to get started.
            </p>
            <div className="field">
              <label>Display name</label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                required
                autoComplete="name"
                placeholder="How you'll appear in the leaderboard"
              />
            </div>
            <div className="field">
              <label>Password <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}>(min 10 chars, uppercase, number, symbol)</span></label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required autoComplete="new-password" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Setting up…' : 'Set Up & Continue'}
            </button>
          </form>
        )}

        {mode === 'forgot' && (
          <form className="login-form" onSubmit={handleForgot}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              Enter your email and we'll send a reset code.
            </p>
            <div className="field">
              <label>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Sending…' : 'Send Reset Code'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <button type="button" className="btn-link" onClick={() => { setError(''); setInfo(''); setMode('login'); }}>
                Back to sign in
              </button>
            </p>
          </form>
        )}

        {mode === 'forgotconfirm' && (
          <form className="login-form" onSubmit={handleForgotConfirm}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
              Enter the code from your email and choose a new password.
            </p>
            <div className="field">
              <label>Reset code</label>
              <input type="text" value={code} onChange={e => setCode(e.target.value)} required autoComplete="one-time-code" inputMode="numeric" />
            </div>
            <div className="field">
              <label>New password</label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required autoComplete="new-password" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Resetting…' : 'Reset Password'}
            </button>
            <p style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <button type="button" className="btn-link" onClick={() => { setError(''); setMode('forgot'); }}>
                Resend code
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
